// FormBuilderSectionCard — a section rendered as a CONTAINER card per design:
// header (grip · round brand collapse chevron · section title + "Section" chip
// · "N fields in this section" count · move/duplicate/delete actions) over the
// children the canvas passes in (field cards + drop gaps, or the empty "Drag
// fields here" panel). The section itself is a normal BuilderField (`section`
// row) in the flat model — the canvas derives the grouping; this component
// only renders and dispatches. Header click selects the section (properties
// panel edits sectionTitle etc.); its `collapsed` flag hides the body. Delete
// goes through the canvas's ConfirmModal (fields are deleted with the section).
// The grip ARMS a native reorder drag of the whole block (same idiom as the
// field card) — the payload adds the SECTION_DRAG_MIME flavor so top-level
// gaps accept it and in-section gaps (accept='field') reject it unchanged.

import { useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Icon, IconButton } from '@/components/common';
import { MOVE_DRAG_MIME, SECTION_DRAG_MIME } from './utils/fieldLibrary';
import type { BuilderField } from './utils/builderModel';

interface FormBuilderSectionCardProps {
  section: BuilderField;
  /** Top-level position label (e.g. "5" — children render "5.1", "5.2"…). */
  badge: string;
  /** Number of fields grouped inside (the header count line). */
  count: number;
  /** The first section can't move above the top-level fields. */
  isFirst: boolean;
  isLast: boolean;
  selected: boolean;
  onSelect: () => void;
  onToggleCollapse: () => void;
  /** Move the WHOLE block (row + children) among sibling sections. */
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** This card is the live drag source (canvas ghosts it). */
  dragging: boolean;
  /** A grip drag started/ended — the canvas tracks the dragged id. */
  onDragStateChange: (dragging: boolean) => void;
  /** Body content from the canvas: child cards + gaps, or the empty panel. */
  children: ReactNode;
}

const FormBuilderSectionCard = ({
  section,
  badge,
  count,
  isFirst,
  isLast,
  selected,
  onSelect,
  onToggleCollapse,
  onMove,
  onDuplicate,
  onDelete,
  dragging,
  onDragStateChange,
  children,
}: FormBuilderSectionCardProps) => {
  const { t } = useTranslation();
  // Grip-armed drag of the whole section block (see the field card's notes on
  // arming/disarming and stopPropagation).
  const [armed, setArmed] = useState(false);
  const handleDragStart = (e: DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.setData(MOVE_DRAG_MIME, section.id);
    e.dataTransfer.setData(SECTION_DRAG_MIME, section.id);
    e.dataTransfer.effectAllowed = 'move';
    onDragStateChange(true);
  };
  const handleDragEnd = () => {
    setArmed(false);
    onDragStateChange(false);
  };
  const bp = section.properties.basicProperties as Record<string, unknown>;
  // Untitled reads as a placeholder rather than falling back to "Section", which
  // looked like a real title (and duplicated the Chip beside it). `.trim()` so a
  // whitespace-only title reads as untitled here too, matching the validator.
  const ownTitle = ((bp.sectionTitle as string) ?? '').trim();
  const title = ownTitle || t('forms_builder_untitled_section');
  // Hidden indicator beside the Section chip (design: only this key — the
  // section's other validation toggle, hideOnPdf, shows no flag).
  const hidden =
    'validationProperties' in section.properties &&
    (section.properties.validationProperties as Record<string, unknown>).hidden === true;

  return (
    // Header click selects the section (opens its properties panel); the
    // trailing actions stop propagation, same idiom as the field card.
    <section
      className={[
        'form-builder-section',
        selected && 'form-builder-section--selected',
        dragging && 'form-builder-section--dragging',
      ]
        .filter(Boolean)
        .join(' ')}
      draggable={armed}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseUp={() => setArmed(false)}
      onMouseLeave={() => setArmed(false)}
    >
      <div className='form-builder-section__head' onClick={onSelect}>
        <span
          className='form-builder-section__grip'
          aria-hidden='true'
          onMouseDown={() => setArmed(true)}
        >
          <Icon name='grip-vertical' size={16} />
        </span>
        <button
          type='button'
          className={[
            'form-builder-section__toggle',
            section.collapsed && 'form-builder-section__toggle--collapsed',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={t(
            section.collapsed ? 'forms_builder_field_expand' : 'forms_builder_field_collapse',
          )}
          aria-expanded={!section.collapsed}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
        >
          <Icon name='chevron-down' size={16} />
        </button>
        <div className='form-builder-section__heading'>
          <span className='form-builder-section__title-row'>
            <span className='form-builder-section__num'>{badge}</span>
            <span
              className={[
                'form-builder-section__title',
                !ownTitle && 'form-builder-section__title--untitled',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {title}
            </span>
            <Chip iconStart='layers'>{t('forms_field_section')}</Chip>
            {hidden && <Chip iconStart='eye-off'>{t('forms_builder_section_hidden')}</Chip>}
          </span>
          <span className='form-builder-section__count'>
            {t('forms_builder_section_count', { count })}
          </span>
        </div>

        <div className='form-builder-section__actions' onClick={(e) => e.stopPropagation()}>
          <IconButton
            icon='chevron-up'
            variant='ghost'
            size='sm'
            aria-label={t('forms_builder_field_move_up')}
            disabled={isFirst}
            onClick={() => onMove(-1)}
          />
          <IconButton
            icon='chevron-down'
            variant='ghost'
            size='sm'
            aria-label={t('forms_builder_field_move_down')}
            disabled={isLast}
            onClick={() => onMove(1)}
          />
          <IconButton
            icon='copy'
            variant='ghost'
            size='sm'
            aria-label={t('forms_builder_field_duplicate')}
            onClick={onDuplicate}
          />
          <IconButton
            icon='trash-2'
            variant='ghost'
            size='sm'
            className='form-builder-field__delete'
            aria-label={t('forms_builder_section_delete')}
            onClick={onDelete}
          />
        </div>
      </div>

      {!section.collapsed && <div className='form-builder-section__body'>{children}</div>}
    </section>
  );
};

export default FormBuilderSectionCard;
